import React, { useState, useEffect } from "react";
import { Box, Typography, Alert, CircularProgress, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Section } from "../../ui";
import ConvertsScannerTable from "../../table/ConvertsScannerTable";
import { getConvertsScannerLatestDateData } from "../../../services/Axios";
import { CalendarToday as CalendarTodayIcon } from "@mui/icons-material";
import BondDetailsModal from "./BondDetailsModal";

const ConvertScannerTab = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [selectedBond, setSelectedBond] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchLatestDateData();
  }, []);

  const fetchLatestDateData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getConvertsScannerLatestDateData();
      if (response?.data) {
        setData(response.data);
        setLatestDate(response.latest_date);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Error loading data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Section>
        <Box
          sx={{
            width: "100%",
            p: 4,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: theme.shadows[1],
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </Section>
    );
  }

  const handleExpand = (bondData) => {
    setSelectedBond(bondData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBond(null);
  };

  // Custom action for header (As of Date)
  const customAction = latestDate ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <CalendarTodayIcon color="primary" fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
        As of:
      </Typography>
      <Chip
        label={latestDate}
        color="primary"
        variant="outlined"
        size="small"
        sx={{ fontWeight: 600 }}
      />
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
        ({data.length} records)
      </Typography>
    </Box>
  ) : null;

  return (
    <Section>
      <ConvertsScannerTable
        data={data}
        title="Convert Scanner Data"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        sorting={sorting}
        setSorting={setSorting}
        customAction={customAction}
        onExpand={handleExpand}
      />
      <BondDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        bondData={selectedBond}
      />
    </Section>
  );
};

export default ConvertScannerTab;
